import React, { useState, useRef, KeyboardEvent, ClipboardEvent } from "react";
import "./verifyOtp.css";
import { forgotPassword, verifyOtp } from "../../../services/authAPI";

interface VerifyOtpProps {
  resetEmail: string;
  setDialogView: React.Dispatch<React.SetStateAction<"signin" | "forgot" | "otp" | "reset">>;
}

const OTP_LENGTH = 6;

const VerifyOtp = ({ resetEmail, setDialogView }: VerifyOtpProps) => {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    setError(null);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => {
      updated[i] = ch;
    });
    setOtp(updated);
    const lastIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");

    if (code.length < OTP_LENGTH) {
      setError("Please enter all 6 digits");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await verifyOtp({
        email: resetEmail,
        otp: code,
      });

      // 👉 If success go to reset password screen
      setDialogView("reset");
    } catch (err: any) {
      setError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🔁 Call forgot password again (resends OTP)
      await forgotPassword(resetEmail);

      // reset OTP UI
      setOtp(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='page-wrapper'>
      <main className='main-content'>
        <div className='login-card'>
          {/* Left Panel */}
          <div className='left-panel'>
            <div className='left-panel-content'>
              <h2 className='welcome-title'>
                Check Your Email <span className='heart-icon'>📧</span>
              </h2>
              <p className='welcome-subtitle'>
                We've sent a 6-digit OTP to <strong className='otp-email-highlight'>{resetEmail}</strong>. Enter it
                below to reset your password.
              </p>
            </div>
          </div>

          {/* Right Panel */}
          <div className='right-panel'>
            <h1 className='login-heading'>Verify OTP</h1>
            <p className='login-subheading'>Enter the 6-digit code sent to your email</p>

            <div className='login-form'>
              {/* OTP Boxes */}
              <div className='otp-group'>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type='text'
                    inputMode='numeric'
                    maxLength={1}
                    className={`otp-box${error ? " input-error" : ""}${digit ? " otp-filled" : ""}`}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={loading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {/* Error */}
              {error && <span className='field-error-msg otp-error'>{error}</span>}

              {/* Verify button */}
              <button className={`login-btn${loading ? " loading" : ""}`} onClick={handleVerify} disabled={loading}>
                {loading ? <span className='spinner' /> : <>Verify OTP</>}
              </button>

              {/* Resend */}
              <p className='register-prompt'>
                Didn't receive the code?{" "}
                <a
                  href='#'
                  className='register-link'
                  onClick={(e) => {
                    e.preventDefault();
                    handleResend();
                  }}
                >
                  Resend OTP
                </a>
              </p>

              {/* Change Email */}
              <p className='register-prompt' style={{ marginTop: 6 }}>
                <a
                  href='#'
                  className='register-link'
                  onClick={(e) => {
                    e.preventDefault();
                    setDialogView("forgot");
                  }}
                >
                  Change Email
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyOtp;
